import { makeAutoObservable, runInAction, configure } from 'mobx';
import type { PopulatedMenu } from 'root/types/menusTypes';
import type { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { Address } from '@wix/ambassador-restaurants-operations-v1-operation/types';
import { getSortedArrayByIds } from 'root/utils/utils';
import type { IOrdersSettingsService } from 'root/services/ordersSettingsService';
import { DispatchType } from 'root/types/businessTypes';
import type { DispatchInfo, TimeSlotsPerMenu } from 'root/types/businessTypes';
import { dispatchState } from 'root/states/DispatchState';
import type { MenuAvailability } from '@wix/restaurants-olo-operations-client-commons';
import { TRUNCATE_FROM_THRESHOLD, DEFAULT_MENU_SIZE } from 'root/appConsts/consts';
import { MenuState } from './MenuState';

configure({ isolateGlobalState: true });

export class MenusState {
  private menusMap: Map<string, MenuState> = new Map();
  private allMenus: MenuState[] = []; // Private immutable array of all menus
  menus: MenuState[] = []; // Filtered/sorted menus exposed to UI
  menusOrder: string[] = [];
  hasError = false;
  isMobile = false;
  size = DEFAULT_MENU_SIZE;
  sectionsSize = 0;
  private ordersSettingsService?: IOrdersSettingsService;
  showUnavailableMenus: boolean = true;
  showExpiredMenus: boolean = true;
  unavailableMenusShownLast: boolean = true;
  private menusAvailability: Record<string, MenuAvailability> = {};

  constructor() {
    makeAutoObservable(this);
  }

  setMenus(menus: PopulatedMenu[], menusOrder?: string[], limitMenuItemsValue?: number) {
    runInAction(() => {
      this.allMenus = menus.map((menu) => new MenuState(menu));
      this.menusMap = new Map(this.allMenus.map((menu) => [menu.menuDto.id, menu]));

      if (limitMenuItemsValue) {
        this.limitMenuItems(limitMenuItemsValue);
      }

      const menusSize = this.allMenus.reduce((acc, menu) => acc + menu.size, 0);
      this.sectionsSize = this.allMenus.reduce(
        (acc, menu) => acc + [...menu.sectionMap.keys()].length,
        0
      );

      if (menusSize > TRUNCATE_FROM_THRESHOLD) {
        this.truncate();
      } else {
        this.size = menusSize;
      }

      this.updateMenusAvailability(this.menusAvailability);

      if (menusOrder) {
        this.menusOrder = menusOrder;
      }

      this.sortMenus();
    });
  }

  getMenu(menuId: string) {
    return this.menusMap.get(menuId);
  }

  limitMenuItems(limitMenuItemsValue: number) {
    let remainingItems = limitMenuItemsValue;

    this.allMenus = this.allMenus.filter((menuState) => {
      if (menuState.menu.size === 0) {
        return true;
      }
      if (remainingItems <= 0) {
        return false;
      }

      menuState.menu.sections = menuState.menu.sections.filter((section) => {
        if (!section.itemIds?.length) {
          return true;
        }
        if (remainingItems <= 0) {
          return false;
        }

        const itemCount = section.itemIds.length;
        if (itemCount > remainingItems) {
          section.itemIds = section.itemIds.slice(0, remainingItems);
          remainingItems = 0;
        } else {
          remainingItems -= itemCount;
        }
        return true;
      });

      return true;
    });
  }

  private truncate() {
    let itemsLeft = this.size;
    this.allMenus = this.allMenus.map((menuState) => {
      if (itemsLeft < menuState.menu.size) {
        menuState.setSize(itemsLeft);
        itemsLeft = 0;
      } else {
        itemsLeft -= menuState.size;
        itemsLeft = Math.max(itemsLeft, 0);
        if (menuState.size !== menuState.menu.size) {
          menuState.setSize(menuState.menu.size);
        }
      }
      return menuState;
    });
  }

  setOrdersSettingsService(ordersSettingsService: IOrdersSettingsService) {
    this.ordersSettingsService = ordersSettingsService;
  }

  setMenusAvailability(menusAvailability: Record<string, MenuAvailability>) {
    this.menusAvailability = menusAvailability;
    this.updateMenusAvailability(menusAvailability);
  }

  private updateMenusAvailability(menusAvailability: Record<string, MenuAvailability>) {
    runInAction(() => {
      Object.entries(menusAvailability).forEach(([menuId, availability]) => {
        const menuState = this.menusMap.get(menuId);
        if (menuState) {
          menuState.updateMenuAvailability(availability);
        }
      });
    });
  }

  async updateFirstAvailableTimeSlotsForMenus(operationId?: string, deliveryAddress?: Address) {
    return this.ordersSettingsService
      ?.fetchFirstAvailableTimeSlotsForMenus({
        operationId,
        deliveryAddress,
      })
      .then((timeSlotsPerMenu) => {
        this.setFirstAvailableTimeSlotsForMenus(timeSlotsPerMenu);
      });
  }

  setFirstAvailableTimeSlotsForMenus(timeSlotsPerMenu: TimeSlotsPerMenu) {
    runInAction(() => {
      Object.entries(timeSlotsPerMenu).forEach(([menuId, timeSlots]) => {
        const menuState = this.menusMap.get(menuId);
        if (menuState) {
          const hasNextAvailableTimeslot =
            !!timeSlots[dispatchState.selectedDispatchType.toLowerCase()];
          menuState.updateNextAvailableTimeslot(hasNextAvailableTimeslot);
        }
      });
      this.sortMenus();
    });
  }

  async updateAvailabilityStatus(
    operationId: string | undefined,
    dispatchInfo: DispatchInfo,
    dispatchType: DispatchType
  ) {
    const menuAvailabilityPromise = this.ordersSettingsService
      ?.fetchMenusAvailabilityStatusByDispatchInfo({
        operationId,
        dispatchInfo,
      })
      .then((menusAvailabilityStatus) => {
        this.setAvailabilityStatus(menusAvailabilityStatus);
      });

    const deliveryAddress =
      dispatchType === DispatchType.DELIVERY ? dispatchInfo.address : undefined;
    const timeSlotsForMenusPromise = this.updateFirstAvailableTimeSlotsForMenus(
      operationId,
      deliveryAddress
    );
    await Promise.all([menuAvailabilityPromise, timeSlotsForMenusPromise]);
  }

  setAvailabilityStatus(availabilityStatus: Record<string, AvailabilityStatus>) {
    runInAction(() => {
      Object.entries(availabilityStatus).forEach(([menuId, status]) => {
        const menuState = this.menusMap.get(menuId);
        if (menuState) {
          menuState.updateAvailabilityStatus(status);
        }
      });
      this.sortMenus();
    });
  }

  get sortedMenusDto() {
    const menus = this.menus.map((menu: MenuState) => menu.menuDto);
    return menus;
  }

  setAvailabilitySettings({
    showUnavailableMenus,
    showExpiredMenus,
    unavailableMenusShownLast,
  }: {
    showUnavailableMenus?: boolean;
    showExpiredMenus?: boolean;
    unavailableMenusShownLast?: boolean;
  }) {
    runInAction(() => {
      if (showUnavailableMenus !== undefined) {
        this.showUnavailableMenus = showUnavailableMenus;
      }
      if (showExpiredMenus !== undefined) {
        this.showExpiredMenus = showExpiredMenus;
      }
      if (unavailableMenusShownLast !== undefined) {
        this.unavailableMenusShownLast = unavailableMenusShownLast;
      }

      this.sortMenus();
    });
  }

  private getMenuAvailability(menuState: MenuState) {
    const hasNextAvailabilityMenu = !!menuState.hasNextAvailableTimeslot;
    const isMenuAvailable = menuState.isAvailable;
    return { hasNextAvailabilityMenu, isMenuAvailable };
  }

  private sortMenusByAvailability(menus: MenuState[]): MenuState[] {
    return menus.sort((menu1, menu2) => {
      const menuAvailability_menu1 = this.getMenuAvailability(menu1);
      const menuAvailability_menu2 = this.getMenuAvailability(menu2);

      if (
        (menuAvailability_menu1.isMenuAvailable ||
          menuAvailability_menu1.hasNextAvailabilityMenu) &&
        !menuAvailability_menu2.isMenuAvailable &&
        !menuAvailability_menu2.hasNextAvailabilityMenu
      ) {
        return -1;
      } else if (
        !menuAvailability_menu1.isMenuAvailable &&
        !menuAvailability_menu1.hasNextAvailabilityMenu &&
        (menuAvailability_menu2.isMenuAvailable || menuAvailability_menu2.hasNextAvailabilityMenu)
      ) {
        return 1;
      }
      return 0;
    });
  }

  private filterMenusByAvailabilitySettings(menus: MenuState[]): MenuState[] {
    return menus.filter((menu) => {
      const { isMenuAvailable, hasNextAvailabilityMenu } = this.getMenuAvailability(menu);
      const availabilityData = menu.getScheduledAvailabilityData();
      const hasFutureAvailability = availabilityData.type !== 'none';

      return (
        // Check if menu is available, or if unavailable menus should be shown based on settings
        isMenuAvailable ||
        (this.showUnavailableMenus &&
          (hasNextAvailabilityMenu || hasFutureAvailability || this.showExpiredMenus))
      );
    });
  }

  private sortAndFilterMenusByAvailability(menus: MenuState[]): MenuState[] {
    const filteredMenus = this.filterMenusByAvailabilitySettings(menus);

    if (this.unavailableMenusShownLast) {
      return this.sortMenusByAvailability(filteredMenus);
    }

    return filteredMenus;
  }

  private sortMenus() {
    const sortedMenus = getSortedArrayByIds(this.allMenus, this.menusOrder);

    this.menus = this.sortAndFilterMenusByAvailability(sortedMenus);
  }

  get hasAtLeastOneMenuWithSections() {
    return this.sortedMenusDto.some((menu) => menu.sectionIds.length > 0);
  }

  get isEmpty() {
    return this.sortedMenusDto.length === 0;
  }

  orderMenus(menusOrder: string[] = []) {
    this.menusOrder = menusOrder;
    this.sortMenus();
  }

  setHasError(hasError: boolean) {
    this.hasError = hasError;
  }
}

const menusState = new MenusState();

export { menusState };
